import { ButtonStyleOptions } from './constants';
import { wixColorParam, StyleParamType } from '@wix/tpa-settings';

// Static button style configuration (similar to providerThemeConfig.ts)
export interface ButtonStyleConfig {
  styles: {
    fillColor: { value: string; name: string | null; opacity: number };
    textColor: { value: string; name: string | null; opacity: number };
    borderColor: { value: string; name: string | null; opacity: number };
  };
  structural: {
    cornerRadius: number;
    borderWidth: number;
  };
}

export const BUTTON_STYLE_CONFIG: Record<
  ButtonStyleOptions,
  ButtonStyleConfig
> = {
  [ButtonStyleOptions.RECTANGLE_FILLED]: {
    styles: {
      fillColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
      textColor: {
        value: 'button-color-text-primary',
        name: 'button-color-text-primary',
        opacity: 1,
      },
      borderColor: {
        value: 'button-color-border-primary',
        name: 'button-color-border-primary',
        opacity: 0,
      },
    },
    structural: {
      cornerRadius: 0,
      borderWidth: 0,
    },
  },
  [ButtonStyleOptions.ROUND_RECTANGLE_FILLED]: {
    styles: {
      fillColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
      textColor: {
        value: 'button-color-text-primary',
        name: 'button-color-text-primary',
        opacity: 1,
      },
      borderColor: {
        value: 'button-color-border-primary',
        name: 'button-color-border-primary',
        opacity: 0,
      },
    },
    structural: {
      cornerRadius: 5,
      borderWidth: 0,
    },
  },
  [ButtonStyleOptions.PILL_FILLED]: {
    styles: {
      fillColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
      textColor: {
        value: 'button-color-text-primary',
        name: 'button-color-text-primary',
        opacity: 1,
      },
      borderColor: {
        value: 'button-color-border-primary',
        name: 'button-color-border-primary',
        opacity: 0,
      },
    },
    structural: {
      cornerRadius: 20,
      borderWidth: 0,
    },
  },
  [ButtonStyleOptions.RECTANGLE_OUTLINE]: {
    styles: {
      fillColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 0,
      },
      textColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
      borderColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
    },
    structural: {
      cornerRadius: 0,
      borderWidth: 1,
    },
  },
  [ButtonStyleOptions.ROUND_RECTANGLE_OUTLINE]: {
    styles: {
      fillColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 0,
      },
      textColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
      borderColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
    },
    structural: {
      cornerRadius: 5,
      borderWidth: 1,
    },
  },
  [ButtonStyleOptions.PILL_OUTLINE]: {
    styles: {
      fillColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 0,
      },
      textColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
      borderColor: {
        value: 'button-color-fill-primary',
        name: 'button-color-fill-primary',
        opacity: 1,
      },
    },
    structural: {
      cornerRadius: 20,
      borderWidth: 1,
    },
  },
};

// Helper function to create button style-based color getters (similar to providerColor)
export const buttonStyleColor = (
  buttonStyleParam: any,
  colorProperty: 'fillColor' | 'textColor' | 'borderColor',
) => ({
  type: StyleParamType.Color as const,
  getDefaultValue: (context: any) => {
    const { getStyleParamValue } = context;
    const buttonStyle = getStyleParamValue(
      buttonStyleParam,
    ) as ButtonStyleOptions;
    const config = BUTTON_STYLE_CONFIG[buttonStyle];
    const colorConfig = config?.styles[colorProperty];

    if (!colorConfig) {
      return wixColorParam('button-color-fill-primary', 1)(context);
    }

    // If it's a theme color, use wixColorParam, otherwise return the RGB color directly
    if (colorConfig.name) {
      return wixColorParam(colorConfig.value, colorConfig.opacity)(context);
    } else {
      return colorConfig;
    }
  },
});
